import React from "react";

import { RefreshIcon } from "@packages/ds";
import { useParams } from "react-router-dom";

import { Tooltip } from "ds/Tooltip";
import { loadManifest } from "src/root/pages/pluginApps/loadManifest";
import { PluginManifest } from "src/root/pages/pluginApps/plugin.types";
import { usePluginApps } from "src/root/pages/pluginApps/usePluginApps";
import { usePluginAppsConfiguration } from "src/root/pages/pluginApps/usePluginAppsConfiguration";

import Context from "../components/Context";

import * as S from "./Page.styles";

/** This props are going to be passed down to the component rendered as part of
 * the plugin app */
type ExtraComponentProps = Partial<{
  organizationId: string;
  projectId: string;
  environmentId: string;
}>;

type PluginAppProps = {
  applicationId?: string;
  route: string;
  type: string;
} & ExtraComponentProps;

const PluginApp = ({
  applicationId,
  route,
  type,
  ...props
}: PluginAppProps) => {
  const { appId } = useParams();
  const [apps, setApps, , isLoading] = usePluginApps();
  const [configuration] = usePluginAppsConfiguration();

  const updateApp = async (app: PluginManifest) => {
    try {
      const manifest = await loadManifest(app._self);
      setApps({ ...apps, [manifest.id]: manifest });
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  if (isLoading) return null;

  const id = appId || applicationId;
  const app = id && apps[id];
  if (!app) return null;

  const hook = app.hooks.find(
    hook => hook.route === route && hook.type === type
  );
  if (!hook) return null;

  if (configuration?.isDevelopmentModeEnabled)
    return (
      <S.ContextWrapper>
        <S.ReloadButton variant="secondary" onClick={() => updateApp(app)}>
          <Tooltip tooltip="Reinstall the plugin app">
            <RefreshIcon />
          </Tooltip>
        </S.ReloadButton>
        <Context
          manifest={app}
          entrypoint={hook.entrypoint || "Main"}
          {...props}
        />
      </S.ContextWrapper>
    );

  return (
    <Context manifest={app} entrypoint={hook.entrypoint || "Main"} {...props} />
  );
};

export default PluginApp;
